import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDepartmentName } from "@/directory/service/colabCorpCode_generator";

export interface DepartmentOption {
  id: string;
  name: string;
  number: number;
}

interface ComboBoxDepartmentSelectProps {
  departments: DepartmentOption[];
  value: string;
  placeholder?: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}

export function ComboBoxDepartmentSelect({
  departments,
  value,
  placeholder = "Seleccionar departamento",
  onChange,
  disabled = false,
}: ComboBoxDepartmentSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selectedDept = departments.find((d) => d.id === value);
  const selectedLabel = selectedDept
    ? formatDepartmentName(selectedDept.name)
    : placeholder;

  const filteredDepartments = departments.filter((dept) => {
    const searchLower = search.toLowerCase();
    const formattedName = formatDepartmentName(dept.name).toLowerCase();
    return formattedName.includes(searchLower);
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) return;
    setOpen(nextOpen);
    if (!nextOpen) setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between"
        >
          {selectedLabel}
          <ChevronsUpDown className="opacity-50 ml-2 h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput
            placeholder="Buscar departamento..."
            className="h-9"
            value={search}
            onValueChange={(val) => setSearch(val)}
          />
          <CommandList>
            <CommandEmpty>No se encontraron resultados.</CommandEmpty>
            <CommandGroup>
              {filteredDepartments.map((dept) => (
                <CommandItem
                  className="cursor-pointer"
                  key={dept.id}
                  value={dept.name}
                  onSelect={() => {
                    onChange(dept.id === value ? '' : dept.id);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  {formatDepartmentName(dept.name)}
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4",
                      value === dept.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}